'use client';

import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import { CloseIcon, HistoryIcon } from '@/shared/ui/display/EmojiIcon';
import { TabAwareModal, useTabContainer } from '@/shared/ui/overlay/Modal';
import { useContainerBreakpoint } from '@/shared/lib/useContainerBreakpoint';
import { formatEventTimestamp } from './lib/formatTimestamp';
import { TokenizedValue } from './components/TokenizedValue';
import {
  hasTokens,
  extractPlannerOperatorIdsFromFieldHistory,
} from '@/shared/lib/sessionTokens/parseTokens';
import { PlannerOperatorProvider } from './context/PlannerOperatorContext';
import { usePlanners } from '@/entities/planner/model/usePlanners';
import { useManagers } from '@/entities/operator/model/useManagers';
import type { FieldHistory } from './types/sessionTimeline.types';

export interface SessionFieldHistoryModalProps {
  open: boolean;
  onClose: () => void;
  /** BFF에서 구성한 필드 히스토리 */
  fieldHistory: FieldHistory;
}

/**
 * 세션 필드 변경 이력 모달 (공통)
 *
 * - BFF가 구성한 fieldHistory를 그대로 표시
 * - GitHub 스타일 타임라인 UI
 */
export function SessionFieldHistoryModal({
  open,
  onClose,
  fieldHistory,
}: SessionFieldHistoryModalProps) {
  const tabContainer = useTabContainer();
  const isMobile = useContainerBreakpoint(tabContainer, 600);
  const paddingX = isMobile ? 2 : 4;

  const { title, trackedFieldLabels, items, currentFieldValues } = fieldHistory;
  const hasChanges = items.some((i) => i.type === 'changed');

  const { plannerIds, operatorIds } = useMemo(
    () => extractPlannerOperatorIdsFromFieldHistory(fieldHistory),
    [fieldHistory]
  );
  const { planners } = usePlanners({ ids: plannerIds });
  const { managers } = useManagers({ ids: operatorIds });
  const plannerOperatorMaps = useMemo(() => {
    const plannerMap = new Map<string, { name: string }>();
    planners.forEach((p) => plannerMap.set(p.id, { name: p.name }));
    const operatorMap = new Map<string, { name: string }>();
    managers.forEach((m) => operatorMap.set(m.id, { name: m.name }));
    return { plannerMap, operatorMap };
  }, [planners, managers]);

  const getPreviousFieldValues = (index: number): Record<string, string> | null => {
    for (let i = index - 1; i >= 0; i--) {
      const prev = items[i];
      if (prev?.fieldValues) return prev.fieldValues;
    }
    return null;
  };

  const isEmpty = (v: string | undefined) => v == null || String(v).trim() === '';
  const toCompare = (v: string | undefined) => (isEmpty(v) ? '-' : String(v));

  const getChangedFieldsOnly = (
    current: Record<string, string>,
    previous: Record<string, string> | null
  ): Record<string, string> => {
    if (!previous) return current;
    const result: Record<string, string> = {};
    for (const label of trackedFieldLabels) {
      const currVal = toCompare(current[label]);
      const prevVal = toCompare(previous[label]);
      if (currVal !== prevVal) result[label] = currVal;
    }
    return result;
  };

  const renderFieldValues = (
    values: Record<string, string>,
    options?: { onlyChanged?: boolean }
  ) => {
    const labels = options?.onlyChanged
      ? trackedFieldLabels.filter((l) => l in values)
      : trackedFieldLabels;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {labels.map((label) => {
          const val = values[label] ?? '-';
          return (
            <Typography
              key={label}
              variant="body2"
              component="div"
              sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}
            >
              <Box component="span" sx={{ flexShrink: 0 }}>
                {label}:
              </Box>
              {hasTokens(val) ? <TokenizedValue value={val} /> : <Box component="span">{val}</Box>}
            </Typography>
          );
        })}
      </Box>
    );
  };

  return (
    <TabAwareModal
      open={open}
      onClose={onClose}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          <HistoryIcon sx={{ fontSize: 20 }} />
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
            {title}
          </Typography>
          <IconButton size="small" onClick={onClose} sx={{ ml: 'auto', color: 'text.secondary' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      }
      maxWidth="sm"
      fullWidth
      titleProps={{
        sx: {
          py: 2,
          px: paddingX,
          background: 'transparent',
        },
      }}
      contentProps={{
        sx: {
          px: paddingX,
          pt: 0,
          pb: 2,
          background: 'transparent',
        },
      }}
    >
      <PlannerOperatorProvider
        plannerMap={plannerOperatorMaps.plannerMap}
        operatorMap={plannerOperatorMaps.operatorMap}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {items.map((item, index) => {
            const isLast = index === items.length - 1;
            const isLastChange = item.type === 'changed' && isLast && hasChanges;

            return (
              <Box
                key={`${item.type}-${item.label}-${item.occurredAt}`}
                sx={{
                  display: 'flex',
                  gap: 2,
                  py: 2,
                  borderBottom: isLast ? 'none' : '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Chip
                    label={item.label}
                    size="small"
                    color={
                      item.type === 'initial' ? 'primary' : isLastChange ? 'success' : 'default'
                    }
                    variant={item.type === 'initial' || isLastChange ? 'filled' : 'outlined'}
                    sx={{ fontSize: '11px', mb: 1 }}
                  />
                  {!isLast ? (
                    <Box
                      sx={{
                        width: 2,
                        flex: 1,
                        minHeight: 24,
                        bgcolor: 'divider',
                        borderRadius: 1,
                      }}
                    />
                  ) : null}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '12px', display: 'block', mb: 0.5 }}
                  >
                    {formatEventTimestamp(item.occurredAt)}
                  </Typography>
                  {item.type === 'initial' && item.fieldValues && (
                    <>{renderFieldValues(item.fieldValues)}</>
                  )}
                  {item.type === 'changed' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {item.metadata &&
                        (['변경 메모', '요청자', '클라이언트'] as const)
                          .filter((label) => label in item.metadata!)
                          .map((label) => {
                            const val = item.metadata![label];
                            const displayVal = val || '-';
                            return (
                              <Typography
                                key={label}
                                variant="body2"
                                component="div"
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  ...(label === '요청자'
                                    ? { color: 'text.secondary', fontSize: '12px' }
                                    : {}),
                                }}
                              >
                                <Box component="span" sx={{ flexShrink: 0 }}>
                                  {label}:
                                </Box>
                                <TokenizedValue value={displayVal} />
                              </Typography>
                            );
                          })}
                      {(() => {
                        const current =
                          item.fieldValues ?? (isLastChange ? currentFieldValues : null);
                        if (!current) return null;
                        const prev = getPreviousFieldValues(index);
                        const changedOnly = getChangedFieldsOnly(current, prev);
                        return (
                          Object.keys(changedOnly).length > 0 &&
                          renderFieldValues(changedOnly, { onlyChanged: true })
                        );
                      })()}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
        {items.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              변경 이력이 없습니다.
            </Typography>
          </Box>
        )}
      </PlannerOperatorProvider>
    </TabAwareModal>
  );
}
